import type { Result, Results } from '@orama/orama'
import type { DocsDocument, ReturningProps } from '../types'

export type SearchResultGroups = [string | null, Array<Result<DocsDocument>>][]

export type SearchResultFacets = Record<string, number>

export interface PathToSectionResult {
  name?: string
  isSubSection?: boolean
}

export function groupSearchResultsBySection(
  searchResults: Results<DocsDocument>,
  returningProps: ReturningProps
): SearchResultGroups {
  if (searchResults === null) return []

  const grouped = returningProps.section
    ? searchResults.hits.reduce<Record<string, { items: Result<DocsDocument>[]; count: number | undefined }>>(
        (acc, curr) => {
          const property = curr.document[returningProps.section]
          if (property) {
            if (!acc[property]) {
              acc[property] = { items: [], count: 0 }
            }

            acc[property].items.push(curr)
            acc[property].count = searchResults.facets?.[returningProps.section]?.values?.[property]
          }
          return acc
        },
        {
          all: {
            items: searchResults.hits,
            count: searchResults.count
          }
        }
      )
    : {
        all: {
          items: searchResults.hits,
          count: searchResults.count
        }
      }

  const hasSections = Object.keys(grouped).length > 1

  const groupBySection = Object.keys(grouped).reduce<SearchResultGroups>((acc, key) => {
    if (key === 'all') return acc
    acc.push([key, grouped[key].items])

    return acc
  }, [])

  return hasSections ? groupBySection : [[null, grouped.all.items]]
}

export function removeHash(text: string): string {
  return text.replace(/#.*$/, '')
}

const pathToSectionCache = new Map<string, PathToSectionResult>()

export function pathToSection(path: string, title = ''): PathToSectionResult {
  if (!path) return { name: title, isSubSection: false }

  const hasHash = path.includes('#')
  const cacheKey = `${path}:${title}`
  const cacheValue = { name: '', isSubSection: hasHash }

  if (pathToSectionCache.has(cacheKey)) {
    return pathToSectionCache.get(cacheKey) || {}
  }

  const fullTitle = title.replace(/\n/, '')
  const isIndexPage = path.endsWith('/index') || path.includes('/index#')
  const parts = path.split('/')

  if (isIndexPage) {
    const pageName = unslugify(parts[parts.length - 2])
    const previousPageName = unslugify(parts[parts.length - 3])

    if (pageName.toLowerCase() === fullTitle.toLowerCase()) {
      cacheValue.name = previousPageName

      pathToSectionCache.set(cacheKey, cacheValue)

      return cacheValue
    }

    cacheValue.name = pageName

    pathToSectionCache.set(cacheKey, cacheValue)

    return cacheValue
  }

  const pageName = unslugify(parts[parts.length - 1].replace(/#.+$/, ''))
  const previousPageName = unslugify(parts[parts.length - 2])

  if (pageName.toLowerCase() === fullTitle.toLowerCase()) {
    cacheValue.name = previousPageName

    pathToSectionCache.set(cacheKey, cacheValue)

    return cacheValue
  }
  cacheValue.name = pageName

  pathToSectionCache.set(cacheKey, cacheValue)

  return cacheValue
}

export function unslugify(slug: string) {
  let text = slug
    ?.replace(/(-|_)/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()

  text = text?.replace(/\b[a-z]/g, (letter) => letter.toUpperCase())

  return text
}
